import { Stack, Typography } from '@mui/material';
import { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';
import {
  gridPageCountSelector,
  GridPagination,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { FunctionComponent } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';

const ActionsComponent: FunctionComponent<TablePaginationActionsProps> = ({
  page,
  onPageChange,
  rowsPerPage,
  count,
}) => {
  const havePreviousPage = page > 0;
  const totalPossiblePages = Math.ceil(count / rowsPerPage) - 1;
  const haveNextPage = page < totalPossiblePages;
  return (
    <Stack spacing={0} direction="row" flexWrap="nowrap">
      <IconButton
        variant="transparent"
        size="sm"
        icon={ChevronLeftIcon}
        disabled={!havePreviousPage}
        onClick={(event) => {
          onPageChange(event, page - 1);
        }}
        ariaLabel="Previous page"
      />
      <IconButton
        variant="transparent"
        size="sm"
        icon={ChevronRightIcon}
        disabled={!haveNextPage}
        onClick={(event) => onPageChange(event, page + 1)}
        ariaLabel="Next page"
      />
    </Stack>
  );
};

function PaginatedTableFooter({
  alwaysShowPaginationFooter,
}: {
  alwaysShowPaginationFooter: boolean;
}) {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  if (!alwaysShowPaginationFooter && pageCount <= 1) {
    return null;
  }

  return (
    <GridPagination
      component="div" // defaults to <p>, which throws a validation error on Typography subtitle1, which uses <h6>
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        pt: 2,
      }}
      labelDisplayedRows={({ from, to, count }) => (
        <Typography variant="body-xs" component="span">
          Displaying {from}-{to} of {count}
        </Typography>
      )}
      ActionsComponent={ActionsComponent}
    />
  );
}

export function DefaultPaginatedTableFooter() {
  return <PaginatedTableFooter alwaysShowPaginationFooter={false} />;
}

export function AlwaysVisiblePaginatedTableFooter() {
  return <PaginatedTableFooter alwaysShowPaginationFooter />;
}
